// Weight <-> volume for cooking. A gram-to-cup conversion only exists for a
// given ingredient, so every conversion goes through its density (grams per
// US cup). Flour is spooned and leveled, brown sugar packed.

export type CookingUnit = 'g' | 'kg' | 'oz' | 'lb' | 'ml' | 'cup' | 'tbsp' | 'tsp';

export const COOKING_UNITS: Array<{ id: CookingUnit; label: string; kind: 'mass' | 'volume' }> = [
  { id: 'g', label: 'grams', kind: 'mass' },
  { id: 'kg', label: 'kilograms', kind: 'mass' },
  { id: 'oz', label: 'ounces (weight)', kind: 'mass' },
  { id: 'lb', label: 'pounds', kind: 'mass' },
  { id: 'cup', label: 'cups (US)', kind: 'volume' },
  { id: 'tbsp', label: 'tablespoons', kind: 'volume' },
  { id: 'tsp', label: 'teaspoons', kind: 'volume' },
  { id: 'ml', label: 'milliliters', kind: 'volume' },
];

const GRAMS: Partial<Record<CookingUnit, number>> = {
  g: 1,
  kg: 1000,
  oz: 28.349523125,
  lb: 453.59237,
};
// US customary cup = 236.5882365 ml; 16 tbsp or 48 tsp to the cup.
const ML: Partial<Record<CookingUnit, number>> = {
  ml: 1,
  cup: 236.5882365,
  tbsp: 14.78676478,
  tsp: 4.92892159,
};

/** Grams per US cup. */
export const INGREDIENTS: Array<{ id: string; name: string; gramsPerCup: number }> = [
  { id: 'water', name: 'Water', gramsPerCup: 236.6 },
  { id: 'flour', name: 'All-purpose flour', gramsPerCup: 120 },
  { id: 'bread-flour', name: 'Bread flour', gramsPerCup: 127 },
  { id: 'whole-wheat-flour', name: 'Whole wheat flour', gramsPerCup: 113 },
  { id: 'sugar', name: 'Granulated sugar', gramsPerCup: 198 },
  { id: 'brown-sugar', name: 'Brown sugar (packed)', gramsPerCup: 213 },
  { id: 'powdered-sugar', name: 'Powdered sugar', gramsPerCup: 113 },
  { id: 'butter', name: 'Butter', gramsPerCup: 227 },
  { id: 'milk', name: 'Milk', gramsPerCup: 242 },
  { id: 'oil', name: 'Vegetable oil', gramsPerCup: 218 },
  { id: 'honey', name: 'Honey', gramsPerCup: 336 },
  { id: 'oats', name: 'Rolled oats', gramsPerCup: 89 },
  { id: 'rice', name: 'White rice (uncooked)', gramsPerCup: 185 },
  { id: 'cocoa', name: 'Cocoa powder', gramsPerCup: 84 },
  { id: 'salt', name: 'Table salt', gramsPerCup: 288 },
  { id: 'chocolate-chips', name: 'Chocolate chips', gramsPerCup: 170 },
];

export function convertCooking(
  value: number,
  from: CookingUnit,
  to: CookingUnit,
  gramsPerCup: number,
): number {
  const gPerMl = gramsPerCup / (ML.cup as number);
  const grams =
    GRAMS[from] !== undefined
      ? value * (GRAMS[from] as number)
      : value * (ML[from] as number) * gPerMl;
  return GRAMS[to] !== undefined
    ? grams / (GRAMS[to] as number)
    : grams / gPerMl / (ML[to] as number);
}

const KITCHEN_FRACTIONS: Array<[number, string]> = [
  [0, ''],
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [3 / 8, '⅜'],
  [1 / 2, '½'],
  [5 / 8, '⅝'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [7 / 8, '⅞'],
  [1, ''],
];

/** "about 1 ⅓" for a cup/spoon amount, or null when no kitchen fraction is close. */
export function kitchenFraction(value: number): string | null {
  if (!(value > 0) || value > 100) return null;
  const whole = Math.floor(value);
  const rest = value - whole;
  let best = KITCHEN_FRACTIONS[0];
  for (const f of KITCHEN_FRACTIONS) if (Math.abs(f[0] - rest) < Math.abs(best[0] - rest)) best = f;
  if (Math.abs(best[0] - rest) > 0.03) return null;
  const w = best[0] === 1 ? whole + 1 : whole;
  if (!w && !best[1]) return null;
  return [w || '', best[1]].filter(Boolean).join(' ');
}
